import React, { useEffect, useState } from "react";
import axios from "../utils/axiosConfig";
import { useParams } from "react-router-dom";
import { 
  Container, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, 
  TableRow, Button, Alert 
} from "@mui/material";
import { useAuth } from "../context/AuthContext";
import { useValidation } from '../context/ValidationContext';

import { handleApiError } from '../utils/apiErrorHandler';

import { useRegistrationValidation } from '../hooks/useRegistrationValidation';
import { useRegistrationStatus } from '../hooks/useRegistrationStatus';
import RegistrationStatusAlert from '../components/RegistrationStatusAlert';
import { toast } from 'react-toastify';

interface Judge {
  _id: string;
  firstname: string;
  surname: string;
  license: string;
}

interface JudgeRegistration {
  _id: string;
  judge: Judge;
  type: "Elite" | "Basis";
  position: string[];
}

interface AthleteRegistration {
  competitionGroup?: string;
  [key: string]: any;
}

interface CompetitionGroup {
  _id: string;
  type: "Elite" | "Basis";
  [key: string]: any;
}

interface JudgeRule {
  minAthletes: number;
  maxAthletes: number;
  requiredJudges: number;
  type: "Elite" | "Basis";
}

const positionsList = ["Egal", "WKL", "Haltung", "SWK", "WKL-Assistenz"]; //, "ToF/HD"];

const JudgeRegistrationPage: React.FC = () => {
  const { competitionId } = useParams();
  const clubId = useAuth();
  //const { refreshValidation } = useValidation();
  const { refreshValidation } = useRegistrationValidation();

  const [registeredJudges, setRegisteredJudges] = useState<JudgeRegistration[]>([]);
  const [availableJudges, setAvailableJudges] = useState<Judge[]>([]);
  const [judgeRules, setJudgeRules] = useState<JudgeRule[]>([]);
  const [loading, setLoading] = useState(true);

  const [athleteCounts, setAthleteCounts] = useState<{Elite: number, Basis: number}>({ Elite: 0, Basis: 0 });
    
  const [onlyBasisAvailable, setOnlyBasisAvailable] = useState<boolean>(false);

  const registrationStatus = useRegistrationStatus(competitionId);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const regResponse = await axios.get(`/api/judge-registrations/${competitionId}`);
        setRegisteredJudges(regResponse.data);
        
        const judgeResponse = await axios.get(`/api/judges`);
        setAvailableJudges(judgeResponse.data);

        const rulesResponse = await axios.get(`/api/competitions/${competitionId}/judge-rules`);
        setJudgeRules(rulesResponse.data);
        
        // Add this section to fetch athlete data
        const athleteResponse = await axios.get(`/api/registrations/${competitionId}/${clubId.clubId}`);
        const athleteData = athleteResponse.data;
        
        // Get competition details to access competitionGroups
        const competitionResponse = await axios.get(`/api/competitions/${competitionId}`);
        const competition = competitionResponse.data;
        setOnlyBasisAvailable(competition.onlyBasisRegistrationAvailable || false);
        
        // Count athletes by type
        const eliteCount = athleteData.filter((reg: AthleteRegistration) => {
          const group = competition.competitionGroups.find(
            (g: CompetitionGroup) => g._id.toString() === reg.competitionGroup?.toString()
          );
          return group?.type === "Elite";
        }).length;
        
        const basicCount = athleteData.filter((reg: AthleteRegistration) => {
          const group = competition.competitionGroups.find(
            (g: CompetitionGroup) => g._id.toString() === reg.competitionGroup?.toString()
          );
          return group?.type === "Basis";
        }).length;
        
        setAthleteCounts({ Elite: eliteCount, Basis: basicCount });
        
        
        refreshValidation();
        setLoading(false);
      } catch (error) {
        handleApiError(error, "Fehler beim Laden der Kampfrichter-Daten:");
      }
    };

    fetchData();
  }, [competitionId, clubId]);

  const registerJudge = async (judgeId: string, type: "Elite" | "Basis", position: string[]) => {
    try {
      // Check if registration is closed
      if (registrationStatus.isClosed) {
        toast.error('Die Meldung ist geschlossen. Änderungen sind nicht mehr möglich.');
        return;
      }
      const response = await axios.post(`/api/judge-registrations`, {
        competition: competitionId,
        club: clubId,
        judge: judgeId,
        type,
        position
      });
      // Prüfen, ob judge-Daten vorhanden sind, falls nicht aus availableJudges ergänzen
      const fullJudge = availableJudges.find(j => j._id === judgeId) || response.data.judge;
      setRegisteredJudges([...registeredJudges, { ...response.data, judge: fullJudge }]);
      refreshValidation();
    } catch (error: any) {
      handleApiError(error, "Fehler beim Anmelden des Kampfrichters.");
    }
  };

  const unregisterJudge = async (registrationId: string) => {
    try {
      // Check if registration is closed
      if (registrationStatus.isClosed) {
        toast.error('Die Meldung ist geschlossen. Änderungen sind nicht mehr möglich.');
        return;
      }
      await axios.delete(`/api/judge-registrations/${registrationId}`);
      setRegisteredJudges(registeredJudges.filter(j => j._id !== registrationId));
      refreshValidation();
    } catch (error: any) {
      handleApiError(error, "Fehler beim Abmelden des Kampfrichters.");
    }
  };

  const handlePositionChange = async (regId: string, position: string) => {
      // Check if registration is closed
      if (registrationStatus.isClosed) {
        toast.error('Die Meldung ist geschlossen. Änderungen sind nicht mehr möglich.');
        return;
      }
    const updatedJudges = registeredJudges.map(j =>
      j._id === regId
        ? { ...j, position: j.position.includes(position) ? j.position.filter(p => p !== position) : [...j.position, position] }
        : j
    );
    setRegisteredJudges(updatedJudges);
    
    // API-Call zur Speicherung der Änderungen
    try {
      const updatedJudge = updatedJudges.find(j => j._id === regId);
      await axios.patch(`/api/judge-registrations/${regId}`, { position: updatedJudge?.position });
    } catch (error: any) {
      handleApiError(error, "Fehler beim Speichern der Positionen.");
    }
  };

  const isJudgeRegistered = (judgeId: string, type: "Elite" | "Basis") => {
    return registeredJudges.some(j => j.judge._id === judgeId && j.type === type);
  };

  const getRequiredJudges = (type: "Elite" | "Basis") => {
    const athleteCount = athleteCounts[type];
    // Find matching rule based on athlete count
    const applicableRule = judgeRules.find(
      rule => (rule.type === type || !rule.type) && 
              athleteCount >= rule.minAthletes && 
              athleteCount <= rule.maxAthletes
    );
    return applicableRule ? applicableRule.requiredJudges : 0;
  };

  const isJudgesSufficient = (type: "Elite" | "Basis") => {
    const required = getRequiredJudges(type);
    const registered = registeredJudges.filter(j => j.type === type).length;
    return registered >= required;
  };

  return (
    <Container component="main" maxWidth="md">
      <Paper elevation={3} sx={{ padding: 3 }}>
        <Typography variant="h5" gutterBottom>
          Kampfrichter-Meldung
        </Typography>

      {/* Registration Status Alert */}
      <RegistrationStatusAlert competitionId={competitionId || ''} />
  
        {/* 🔹 Kampfrichter-Tabellen für Elite & Basis */}
        {(["Elite", "Basis"] as const).map((type) => (
          <div key={type}>
            <Typography variant="h6" sx={{ mt: 2 }}>
              {type} Kampfrichter ({registeredJudges.filter((j) => j.type === type).length} / {getRequiredJudges(type as "Elite" | "Basis")})
            </Typography>
  
            {!isJudgesSufficient(type as "Elite" | "Basis") && (
              <Alert severity="warning">Es fehlen noch Kampfrichter für {type}!</Alert>
            )}
  
            <TableContainer component={Paper} sx={{ mt: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Lizenz</TableCell>
                    <TableCell>Wunschposition</TableCell>
                    <TableCell>Aktion</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {registeredJudges
                    .filter((j) => j.type === type)
                    .map((reg) => (
                      <TableRow key={reg._id}>
                        <TableCell>{reg.judge.firstname} {reg.judge.surname}</TableCell>
                        <TableCell>{reg.judge.license}</TableCell>
                        <TableCell>
                          {positionsList.map((pos) => (
                            <label key={pos} style={{ marginRight: "10px", display: "inline-flex", alignItems: "center" }}>
                              <input
                                type="checkbox"
                                checked={reg.position.includes(pos)}
                                onChange={() => handlePositionChange(reg._id, pos)}
                                disabled={registrationStatus.isClosed}
                              />
                              {pos}
                            </label>
                          ))}
                        </TableCell>
                        <TableCell>
                          <Button variant="contained" color="error" size="small" onClick={() => unregisterJudge(reg._id)} disabled={registrationStatus.isClosed}>
                            Abmelden
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        ))}
  
        {/* 🔹 Verfügbare Kampfrichter */}
        <Typography variant="h6" sx={{ mt: 3 }}>
          Verfügbare Kampfrichter
        </Typography>
        <TableContainer component={Paper} sx={{ mt: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Lizenz</TableCell>
                <TableCell>Aktion</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {availableJudges.map((judge) => (
                <TableRow key={judge._id}>
                  <TableCell>{judge.firstname} {judge.surname}</TableCell>
                  <TableCell>{judge.license}</TableCell>
                  <TableCell>
                    {onlyBasisAvailable ? (
                      // 🆕 Nur ein Button wenn onlyBasisAvailable true ist
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => registerJudge(judge._id, "Basis", ["Egal"])}
                        disabled={isJudgeRegistered(judge._id, "Basis") || registrationStatus.isClosed}
                      >
                        Anmelden
                      </Button>
                    ) : (
                      // 🔹 Normale zwei Buttons wenn onlyBasisAvailable false/undefined ist
                      <>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => registerJudge(judge._id, "Basis", ["Egal"])}
                          disabled={isJudgeRegistered(judge._id, "Basis") || registrationStatus.isClosed}
                        >
                          Basis anmelden
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          sx={{ ml: 1 }}
                          onClick={() => registerJudge(judge._id, "Elite", ["Egal"])}
                          disabled={isJudgeRegistered(judge._id, "Elite") || registrationStatus.isClosed}
                        >
                          Elite anmelden
                        </Button>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Container>
  );
  


  /*  <div>
      <h2>Kampfrichter-Meldung</h2>
      {loading ? (
        <p>Lade Daten...</p>
      ) : (
        <>
          <h3>Gemeldete Kampfrichter (Elite) {isJudgesSufficient("Elite") ? "" : "⚠️"}</h3>
          <p>Benötigte Kampfrichter: {getRequiredJudges("Elite")}</p>
          {!isJudgesSufficient("Elite") && <p>Es fehlen noch Kampfrichter für Elite. Benötigte Kampfrichter</p> }
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Lizenz</th>
                <th>Wunschposition</th>
                <th>Aktion</th>
              </tr>
            </thead>
            <tbody>
              {registeredJudges.filter(j => j.type === "Elite").map((reg) => (
                <tr key={reg._id}>
                  <td>{reg.judge.firstname} {reg.judge.surname}</td>
                  <td>{reg.judge.license}</td>
                  <td>
                    {positionsList.map(pos => (
                      <label key={pos} style={{ marginRight: "10px" }}>
                        <input
                          type="checkbox"
                          checked={reg.position.includes(pos)}
                          onChange={() => handlePositionChange(reg._id, pos)}
                        />
                        {pos}
                      </label>
                    ))}
                  </td>
                  <td>
                    <button onClick={() => unregisterJudge(reg._id)}>Abmelden</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <h3>Gemeldete Kampfrichter (Basis) {isJudgesSufficient("Basis") ? "" : "⚠️"}</h3>
          <p>Benötigte Kampfrichter: {getRequiredJudges("Basis")}</p>
          {!isJudgesSufficient("Basis") && <p>Es fehlen noch Kampfrichter für Basis.</p>}
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Lizenz</th>
                <th>Wunschposition</th>
                <th>Aktion</th>
              </tr>
            </thead>
            <tbody>
              {registeredJudges.filter(j => j.type === "Basis").map((reg) => (
                <tr key={reg._id}>
                  <td>{reg.judge.firstname} {reg.judge.surname}</td>
                  <td>{reg.judge.license}</td>
                  <td>
                    {positionsList.map(pos => (
                      <label key={pos} style={{ marginRight: "10px" }}>
                        <input
                          type="checkbox"
                          checked={reg.position.includes(pos)}
                          onChange={() => handlePositionChange(reg._id, pos)}
                        />
                        {pos}
                      </label>
                    ))}
                  </td>
                  
                  <td>
                    <button onClick={() => unregisterJudge(reg._id)}>Abmelden</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <h3>Verfügbare Kampfrichter</h3>
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Lizenz</th>
                <th>Aktion</th>
              </tr>
            </thead>
            <tbody>
              {availableJudges.map((judge) => (
                <tr key={judge._id}>
                  <td>{judge.firstname} {judge.surname}</td>
                  <td>{judge.license}</td>
                  <td>
                    <button
                      onClick={() => registerJudge(judge._id, "Basis", ["Egal"]) }
                      disabled={isJudgeRegistered(judge._id, "Basis")}
                    >Basis anmelden</button>
                    <button
                      onClick={() => registerJudge(judge._id, "Elite", ["Egal"]) }
                      disabled={isJudgeRegistered(judge._id, "Elite")}
                    >Elite anmelden</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );*/
};

export default JudgeRegistrationPage;
